import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, CalendarClock, MapPin } from "lucide-react";
import { scrollToId } from "@/lib/scroll";
import { useSiteContent } from "@/lib/siteContent";

const EASE = [0.22, 1, 0.36, 1];

export default function Hero() {
  const content = useSiteContent();
  const LINES = content.heroLines.map((l) => ({
    text: l.text,
    cls: l.accent ? "text-[#3AB0E1]" : "text-[#263562]",
  }));
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 120]);
  const glowY = useTransform(scrollYProgress, [0, 1], [0, -80]);
  const textY = useTransform(scrollYProgress, [0, 1], [0, 60]);

  return (
    <section
      id="hero"
      data-testid="hero-section"
      ref={ref}
      className="relative min-h-screen flex flex-col justify-center overflow-hidden pt-28 pb-16"
    >
      <div className="hero-grid absolute inset-0" aria-hidden="true" />
      <motion.div
        style={{ y: glowY }}
        aria-hidden="true"
        className="absolute -top-40 left-1/2 -translate-x-1/2 w-[900px] h-[600px] rounded-full bg-[radial-gradient(circle,rgba(58,176,225,0.14)_0%,transparent_65%)]"
      />

      <div className="relative max-w-7xl mx-auto px-6 lg:px-10 w-full grid lg:grid-cols-[1.15fr_0.85fr] gap-16 items-center">
        <motion.div style={{ y: textY }}>
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.35, ease: EASE }}
            className="flex items-center gap-3 mb-8"
          >
            <MapPin className="w-3.5 h-3.5 text-[#3AB0E1]" />
            <span className="font-mono-tech text-[11px] uppercase tracking-[0.3em] text-[#263562]/80">
              Hostech Solutions Ltd — Nairobi, Kenya
            </span>
          </motion.div>

          <h1
            data-testid="hero-title"
            className="font-display font-extrabold tracking-tight leading-[1.02] text-4xl sm:text-6xl lg:text-7xl"
          >
            {LINES.map((line, i) => (
              <span key={i} className="block overflow-hidden pb-1">
                <motion.span
                  className={`block ${line.cls}`}
                  initial={{ y: "110%" }}
                  animate={{ y: 0 }}
                  transition={{ duration: 0.9, delay: 0.5 + i * 0.14, ease: EASE }}
                >
                  {line.text}
                </motion.span>
              </span>
            ))}
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 1.15, ease: EASE }}
            className="mt-8 max-w-xl text-base sm:text-lg leading-relaxed text-[#5A6B85] font-light"
          >
            {content.heroSub}
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 1.35, ease: EASE }}
            className="mt-10 flex flex-wrap items-center gap-4"
          >
            <button
              data-testid="hero-cta-primary"
              onClick={() => scrollToId("#flagships")}
              className="group flex items-center gap-3 bg-[#263562] hover:bg-[#3AB0E1] text-white font-semibold px-7 py-4 rounded-full transition-all duration-300 hover:shadow-[0_10px_40px_rgba(58,176,225,0.4)]"
            >
              Explore the HostPoint Suite
              <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform duration-300" />
            </button>
            <button
              data-testid="hero-cta-secondary"
              onClick={() => scrollToId("#contact")}
              className="group flex items-center gap-3 border border-[#263562]/20 hover:border-[#3AB0E1] text-[#263562] hover:text-[#3AB0E1] font-medium px-7 py-4 rounded-full transition-colors duration-300"
            >
              <CalendarClock className="w-4 h-4" />
              Schedule a System Demo
            </button>
          </motion.div>
        </motion.div>

        <motion.div
          style={{ y: imgY }}
          initial={{ opacity: 0, y: 60, rotate: 2 }}
          animate={{ opacity: 1, y: 0, rotate: 0 }}
          transition={{ duration: 1.1, delay: 0.9, ease: EASE }}
          className="relative hidden lg:block"
        >
          <div
            aria-hidden="true"
            className="absolute -inset-10 rounded-full border border-dashed border-[#3AB0E1]/40 animate-spin-slow"
          />
          <div
            aria-hidden="true"
            className="absolute -inset-4 rounded-2xl bg-[radial-gradient(circle_at_50%_0%,rgba(58,176,225,0.18),transparent_70%)]"
          />
          <div className="relative rounded-xl overflow-hidden border border-[#263562]/10 shadow-[0_40px_80px_-20px_rgba(38,53,98,0.28)] bg-white">
            <div className="flex items-center gap-1.5 px-4 py-2.5 bg-[#F5F8FC] border-b border-[#263562]/10">
              <span className="w-2.5 h-2.5 rounded-full bg-[#C7D2E4]" />
              <span className="w-2.5 h-2.5 rounded-full bg-[#C7D2E4]" />
              <span className="w-2.5 h-2.5 rounded-full bg-[#3AB0E1]/70" />
              <span className="ml-3 font-mono-tech text-[10px] text-[#949CA1] tracking-wider">
                app.hostpoint.co.ke/dashboard
              </span>
            </div>
            <img
              src="https://hostpointpos.com/showcase/dashboard.png"
              alt="HostPoint POS live business dashboard"
              className="w-full h-auto block"
              loading="eager"
            />
          </div>
          <motion.div
            initial={{ opacity: 0, x: 40 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.9, delay: 1.5, ease: EASE }}
            className="absolute -bottom-8 -left-10 w-44 rounded-lg overflow-hidden border border-[#3AB0E1]/40 shadow-[0_25px_50px_-12px_rgba(38,53,98,0.3)] bg-white"
          >
            <img
              src="https://hostpointpos.com/showcase/etims.png"
              alt="KRA eTIMS fiscal management in HostPoint"
              className="w-full h-auto block"
              loading="lazy"
            />
            <span className="block px-3 py-2 font-mono-tech text-[9px] uppercase tracking-[0.2em] text-[#263562]">
              KRA eTIMS Ready
            </span>
          </motion.div>
        </motion.div>
      </div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1, delay: 1.8 }}
        className="relative max-w-7xl mx-auto px-6 lg:px-10 w-full mt-20"
      >
        <div className="flex flex-wrap gap-x-8 gap-y-3 border-t border-[#263562]/10 pt-6 font-mono-tech text-[10px] sm:text-[11px] uppercase tracking-[0.25em] text-[#949CA1]">
          {content.ticker.flatMap((t, i) =>
            i === 0
              ? [<span key={i}>{t}</span>]
              : [
                  <span key={`sep-${i}`} className="text-[#3AB0E1]">/</span>,
                  <span key={i}>{t}</span>,
                ]
          )}
        </div>
      </motion.div>
    </section>
  );
}
